class KissBundlePicker {
  constructor(data, vmaps) {
    this.bundles      = data?.bundles || [];
    this.vmaps        = vmaps || {};
    this.split_option = data?.split_option || null;
  }

  /**
   * Find a bundle matching the selected product handles and split value (e.g. Color).
   * Roles are the keys in bundle.components — no regex guessing needed.
   * @param {string[]} selectedHandles - e.g. ["lily-bra-peach", "lily-thong-peach"]
   * @param {string|null} splitValue   - e.g. "Peach", or null when no split option
   * @returns {Object|null}
   */
  findBundle(selectedHandles, splitValue) {
    const matching = this.bundles.filter(bundle => {
      if ((bundle.split_value ?? null) !== (splitValue ?? null)) return false;
      const bundleHandles = Object.values(bundle.components || {});
      if (!bundleHandles.length) return false;
      return bundleHandles.every(h => selectedHandles.includes(h));
    });

    if (!matching.length) return null;
    if (matching.length === 1) return matching[0];

    // Multiple bundles match (user selected multiple panty types).
    // Pick the bundle whose non-bra component was selected FIRST (by index in selectedHandles).
    return matching.reduce((best, bundle) => {
      const nonBraIdx = (b) => Math.min(
        ...Object.entries(b.components || {})
          .filter(([role]) => role !== 'bra')
          .map(([, handle]) => selectedHandles.indexOf(handle))
      );
      return nonBraIdx(bundle) < nonBraIdx(best) ? bundle : best;
    });
  }

  /**
   * Translate component-level option names → bundle-level using bundle.option_map.
   * Safe to call on already-resolved names (idempotent: bundle-level names pass through).
   * @param {Object} bundle
   * @param {Object} rawOptions - { componentOptionName: value }
   * @returns {Object} - { bundleOptionName: value }
   */
  _resolveOptions(bundle, rawOptions) {
    const map = bundle.option_map || {};
    const resolved = {};
    for (const [k, v] of Object.entries(rawOptions)) {
      // If already a bundle-level name, keep as-is (idempotent)
      const bundleName = (bundle.option_order || []).includes(k) ? k : (map[k] || k);
      resolved[bundleName] = v;
    }
    return resolved;
  }

  /**
   * Find a variant inside a bundle by selected options.
   * Accepts both component-level names (translated via bundle.option_map)
   * and already-resolved bundle-level names.
   * Negative ID in vmap means out-of-stock.
   * @param {Object} bundle           - result of findBundle()
   * @param {Object} options          - { optionName: value } (component or bundle level)
   * @returns {{ id: number, available: boolean }|null}
   */
  findVariant(bundle, options) {
    const vmap = bundle ? this.vmaps[bundle.id] : null;
    if (!vmap?.v) return null;
    const resolved = this._resolveOptions(bundle, options);
    const key = (bundle.option_order || []).map(o => resolved[o]).join('|');
    const raw = vmap.v[key];
    if (raw === undefined) return null;
    return { id: Math.abs(raw), available: raw > 0 };
  }

  /**
   * Get available values for one option, respecting already-fixed options.
   * Accepts component-level option names — resolves internally via bundle.option_map.
   * @param {Object} bundle
   * @param {string} optionName      - component or bundle level option name
   * @param {Object} fixedOptions    - already-selected options to filter by
   * @returns {{ value: string, available: boolean }[]}
   */
  getOptionValues(bundle, optionName, fixedOptions = {}) {
    const vmap = bundle ? this.vmaps[bundle.id] : null;
    if (!vmap?.v) return [];

    // Resolve option name and fixed options to bundle level
    const resolvedName = this._resolveOptions(bundle, { [optionName]: '' });
    const bundleOptName = Object.keys(resolvedName)[0];
    const optIdx = (bundle.option_order || []).indexOf(bundleOptName);
    if (optIdx === -1) return [];

    const resolvedFixed = this._resolveOptions(bundle, fixedOptions);

    const seen = new Map();
    for (const [key, raw] of Object.entries(vmap.v)) {
      const parts = key.split('|');
      const matches = (bundle.option_order || []).every((name, i) => {
        if (name === bundleOptName) return true;
        const fixed = resolvedFixed[name];
        return !fixed || parts[i] === fixed;
      });
      if (!matches) continue;
      const value = parts[optIdx];
      const available = raw > 0;
      if (!seen.has(value)) seen.set(value, available);
      else if (available) seen.set(value, true);
    }
    return [...seen.entries()].map(([value, available]) => ({ value, available }));
  }
}
